import React, { useState } from "react";
import { Input } from "../../../../Ecommerce/Shared/Input/Input";
import { SearchParams } from "../../../CollectionNewSearchPage";
import { CollectionNewSearchPageTranslations } from "../../../CollectionNewSearchPageTranslations.csharp";

interface CollectionYearFilterProps {
    minYear: number;
    maxYear: number;
    setMinYear: React.Dispatch<React.SetStateAction<number>>;
    setMaxYear: React.Dispatch<React.SetStateAction<number>>;
    currentMinYear: number;
    currentMaxYear: number;
    searchParams: SearchParams;
    setSearchParams: React.Dispatch<React.SetStateAction<SearchParams>>;
    translations: CollectionNewSearchPageTranslations;
}

export const CollectionYearFilter = ({
    minYear,
    maxYear,
    setMinYear,
    setMaxYear,
    currentMinYear,
    currentMaxYear,
    setSearchParams,
    searchParams,
    translations
}: CollectionYearFilterProps) => {
    const [minYearError, setMinYearError] = useState<string | null>(null);
    const [maxYearError, setMaxYearError] = useState<string | null>(null);

    const validateYear = (year: string, type: "min" | "max"): string | null => {
        const regex = /^\d{4}$/;
        const val = parseInt(year);
        if (!regex.test(year) || isNaN(val) || year === "") return "Skriv inn et gyldig årstall";
        if (type === "min" && val < minYear) return `Året kan ikke være mindre enn ${minYear}`;
        if (type === "max" && val > maxYear) return `Året kan ikke være større enn ${maxYear}`;
        return null;
    };

    const validateRange = (newMin: number, newMax: number, offset = 0): boolean => {
        if (newMin < minYear - offset || newMax > maxYear + offset) return false;
        if (newMin > newMax) return false;
        return true;
    };

    const applyNewRange = (newMin: number, newMax: number) => {
        setMinYear(newMin);
        setMaxYear(newMax);
        setMinYearError(validateYear(String(newMin), "min"));
        setMaxYearError(validateYear(String(newMax), "max"));
        setSearchParams({ ...searchParams, years: `${newMin}|${newMax}` });
    };

    const handleMinYearChange = (val: string) => {
        setMinYear(Number(val));
        const error = validateYear(val, "min");
        setMinYearError(error);
        if (!error) setSearchParams({ ...searchParams, years: `${val}|${currentMaxYear}` });
    };

    const handleMaxYearChange = (val: string) => {
        setMaxYear(Number(val));
        const error = validateYear(val, "max");
        setMaxYearError(error);
        if (!error) setSearchParams({ ...searchParams, years: `${currentMinYear}|${val}` });
    };

    const [nextMinInc, nextMaxInc] = (() => {
        let newMin = currentMinYear + 1;
        let newMax = currentMaxYear;
        if (newMin > newMax) newMax = newMin;
        return [newMin, newMax];
    })();

    const [nextMinDec, nextMaxDec] = [currentMinYear - 1, currentMaxYear];
    const [nextMaxIncMin, nextMaxIncMax] = [currentMinYear, currentMaxYear + 1];
    const [nextMaxDecMin, nextMaxDecMax] = (() => {
        let newMax = currentMaxYear - 1;
        let newMin = currentMinYear;
        if (newMax < newMin) newMin = newMax;
        return [newMin, newMax];
    })();

    const canIncrementMin = validateRange(nextMinInc, nextMaxInc, 1);
    const canDecrementMin = validateRange(nextMinDec, nextMaxDec, 1);
    const canIncrementMax = validateRange(nextMaxIncMin, nextMaxIncMax, 1);
    const canDecrementMax = validateRange(nextMaxDecMin, nextMaxDecMax, 1);

    const incrementMinYear = () => applyNewRange(nextMinInc, nextMaxInc);
    const decrementMinYear = () => applyNewRange(nextMinDec, nextMaxDec);
    const incrementMaxYear = () => applyNewRange(nextMaxIncMin, nextMaxIncMax);
    const decrementMaxYear = () => applyNewRange(nextMaxDecMin, nextMaxDecMax);

    return (
        <div className="CollectionYearFilter">
            <div className="CollectionYearFilter__input-group">
                <legend className="CollectionYearFilter__legend">{translations.filterHeaderYearRangeFrom}</legend>
                <div className="CollectionYearFilter__custom-input">
                    <Input
                        type="number"
                        onChange={handleMinYearChange}
                        value={currentMinYear === 0 ? "" : String(currentMinYear)}
                        isRequired
                        errorValidation={!!minYearError}
                        errorValidationText={minYearError}
                        showError={!!minYearError}
                        label="Årstall fra"
                        hiddenLabel
                    />
                    <span
                        className={
                            "CollectionYearFilter__arrow-up" +
                            (!canIncrementMin ? " CollectionYearFilter__arrow-up--disabled" : "")
                        }
                        onClick={canIncrementMin ? incrementMinYear : undefined}
                    />
                    <span
                        className={
                            "CollectionYearFilter__arrow-down" +
                            (!canDecrementMin ? " CollectionYearFilter__arrow-down--disabled" : "")
                        }
                        onClick={canDecrementMin ? decrementMinYear : undefined}
                    />
                </div>
                <div className="CollectionYearFilter__reset-year">
                    Første arbeid
                    <button
                        className="CollectionYearFilter__reset-year-button"
                        onClick={() => handleMinYearChange(String(minYear))}
                    >
                        {minYear}
                    </button>
                </div>
            </div>
            <div className="CollectionYearFilter__seperator-container">
                <span className="CollectionYearFilter__seperator"></span>
            </div>
            <div className="CollectionYearFilter__input-group">
                <legend className="CollectionYearFilter__legend">{translations.filterHeaderYearRangeTo}</legend>
                <div className="CollectionYearFilter__custom-input">
                    <Input
                        type="number"
                        onChange={handleMaxYearChange}
                        value={currentMaxYear === 0 ? "" : String(currentMaxYear)}
                        isRequired
                        errorValidation={!!maxYearError}
                        errorValidationText={maxYearError}
                        showError={!!maxYearError}
                        label="Årstall til"
                        hiddenLabel
                    />
                    <span
                        className={
                            "CollectionYearFilter__arrow-up" +
                            (!canIncrementMax ? " CollectionYearFilter__arrow-up--disabled" : "")
                        }
                        onClick={canIncrementMax ? incrementMaxYear : undefined}
                    />
                    <span
                        className={
                            "CollectionYearFilter__arrow-down" +
                            (!canDecrementMax ? " CollectionYearFilter__arrow-down--disabled" : "")
                        }
                        onClick={canDecrementMax ? decrementMaxYear : undefined}
                    />
                </div>
                <div className="CollectionYearFilter__reset-year">
                    Siste arbeid
                    <button
                        className="CollectionYearFilter__reset-year-button"
                        onClick={() => handleMaxYearChange(String(maxYear))}
                    >
                        {maxYear}
                    </button>
                </div>
            </div>
        </div>
    );
};